import React from 'react'
import PropTypes from 'prop-types'
import { Button, ButtonGroup, Drawer, Box, Label1, Body1, Caption } from '@sainsburys-tech/fable'
import { DeliveryTwoMan, Trolley, ThumbUp, InfoCircleFilled } from '@sainsburys-tech/icons'
import ArgosPlusLogo from '@/components/ArgosPlusLogo/ArgosPlusLogo'
import { pushEventAndTrack } from '@/javascript/helpers/taggingSerializer'

const ACCOUNT_LOGIN_URL = '/account/login'
const BUTTON_BORDER_RADIUS = 'ds-rounded-[8px]'

export default function FulfilmentPassDrawer({
  isLoggedIn = false,
  isOpen = false,
  onClose = () => {},
  addPassToTrolley,
  isAddingPassToTrolley = false
}) {
  const handleAddToTrolley = async () => {
    await addPassToTrolley()
    onClose()
  }

  const signInBox = (
    <Box
      as='div'
      className='ds-mb-4'
      border={{
        radius: 'md',
        size: 'sm'
      }}
      padding={{
        sm: 'sm'
      }}
      style={{
        backgroundColor: '#E7EFF7',
        borderColor: '#114E8B',
        color: '#114E8B'
      }}
    >
      <div className='ds-flex ds-items-start ds-gap-2'>
        <InfoCircleFilled className='ds-w-8' style={{ color: '#114E8B' }} />
        <div>
          <Body1>Already have Argos Plus?</Body1>
          <Body1>
            <a href={ACCOUNT_LOGIN_URL} className='ds-font-semibold ds-underline' style={{ color: '#114E8B' }}>
              Sign in to enjoy benefits
            </a>
          </Body1>
        </div>
      </div>
    </Box>
  )

  const trackTermsClick = () => {
    pushEventAndTrack({
      eventInfo: {
        eventAction: 'fulfilment pass terms and conditions',
        eventName: 'fulfilmentPass_termsAndConditions'
      }
    })
  }

  const renderContent = () => (
    <div>
      <div className='ds-mb-4 ds-mt-4'>
        <ArgosPlusLogo />
        <Label1 className='ds-mb-0 ds-mt-2'>
          Get a whole year of home delivery for £40 with the Argos Plus delivery pass.
        </Label1>
      </div>

      <div className='ds-mb-4'>
        <div className='ds-flex ds-items-start ds-gap-2'>
          <DeliveryTwoMan className='ds-w-8' style={{ color: '#D42114' }} />
          <div>
            <Label1 className='ds-font-semibold ds-mb-4'>What you want, when you want it</Label1>
            <Body1>Same-day, next-day, and standard delivery are all included. Whichever works best.</Body1>
          </div>
        </div>
      </div>
      <div className='ds-mb-4'>
        <div className='ds-flex ds-items-start ds-gap-2'>
          <ThumbUp className='ds-w-8' style={{ color: '#D42114' }} />
          <div>
            <Label1 className='ds-font-semibold ds-mb-4'>One pass, zero faff</Label1>
            <Body1>From sofas to smartphones, pay once and your delivery costs are covered for a year.</Body1>
          </div>
        </div>
      </div>
      <Body1 className='ds-mb-4'>
        You&apos;re invited to join an exclusive trial of Argos Plus. To ensure that all new features are visible,{' '}
        <a href={ACCOUNT_LOGIN_URL} className='ds-underline' style={{ color: '#114E8B' }}>
          log in
        </a>{' '}
        whenever you shop.
      </Body1>
      <ul className='ds-mb-4' style={{ listStyleType: 'disc', paddingLeft: '1.5em' }}>
        <li>
          <Caption>Orders over £20 qualify for Argos Plus delivery</Caption>
        </li>
        <li>
          <Caption>Excludes clothing and items delivered by a supplier</Caption>
        </li>
        <li>
          <Caption>Delivery slots are subject to stock, postcode and availability</Caption>
        </li>
      </ul>
      <Caption className='ds-mb-4'>
        By signing up, you&apos;re agreeing to these{' '}
        <a
          href='https://help.argos.co.uk/help/terms-and-conditions/terms-&-conditions/3907544526653875560'
          className='ds-underline ds-font-semibold'
          style={{ color: '#114E8B' }}
          onClick={trackTermsClick}
        >
          terms and conditions.
        </a>
      </Caption>
      {!isLoggedIn && signInBox}
    </div>
  )

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position='right'
      divider='both'
      header={{
        label: 'Explore the benefits of Argos Plus',
        size: 'Display1',
        isDismissible: true
      }}
      renderFooter={
        <ButtonGroup isReversed={false} isStacked>
          <Button
            className={BUTTON_BORDER_RADIUS}
            variant='primary'
            icon={<Trolley />}
            onClick={handleAddToTrolley}
            isFullWidth
            isLoading={isAddingPassToTrolley}
            data-testid='fulfilment-pass-add-to-trolley-button'
          >
            Add to trolley
          </Button>
          <Button className={BUTTON_BORDER_RADIUS} variant='secondary' onClick={onClose} isFullWidth>
            No thanks
          </Button>
        </ButtonGroup>
      }
    >
      {renderContent()}
    </Drawer>
  )
}

FulfilmentPassDrawer.propTypes = {
  isLoggedIn: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  addPassToTrolley: PropTypes.func.isRequired,
  isAddingPassToTrolley: PropTypes.bool
}
